import React, { useEffect, useState } from "react";

import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";

import { ButtonLoading, ButtonStandard, Checkbox, InputText, Text } from "@performyard/py-components";

import ContentModal, { ContentModalProps } from "../../../components/Modal/Templates/ContentModal";

import { useEmployeeSelectionObject } from "../../../hooks/useEmployeeSelectionObject";

import { GetUserSelectionUsersProps } from "../../../api/services/defs/user-selection";

type EmployeeSelectGroupsModalProps = ContentModalProps & {
	queryProps: GetUserSelectionUsersProps;
};

const EmployeeSelectGroupsModal = ({ isOpen, handleClose, queryProps }: EmployeeSelectGroupsModalProps) => {
	const { departments, addUsersToSelection, isAddingUsersToSelection } = useEmployeeSelectionObject(queryProps);
	const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
	const [selectedAll, setSelectedAll] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");

	useEffect(() => {
		if (isOpen) {
			setSelectedAll(false);
			setSelectedGroups([]);
			setSearchTerm("");
		}
	}, [isOpen]);

	useEffect(() => {
		if (!departments?.length) {
			return;
		}
		const selectableIds = new Set(
			departments.filter((department) => department.numUsers > 0).map((department) => department._id)
		);
		setSelectedGroups((previous) => previous.filter((id) => selectableIds.has(id)));
	}, [departments]);

	return (
		<ContentModal
			closeOnClickOutside={true}
			isOpen={isOpen}
			handleClose={handleClose}
			title={
				<Text size="sub-heading" weight="semi-bold">
					Add Groups To Selection
				</Text>
			}
			buttons={[
				<div className="w-fit" key="cancel">
					<ButtonStandard type="button" onClick={handleClose} size="large" color="red">
						Cancel
					</ButtonStandard>
				</div>,
				<div className="w-fit" key="select">
					<ButtonLoading
						loading={isAddingUsersToSelection}
						type="button"
						onClick={() => {
							addUsersToSelection({
								groups: selectedGroups,
								selectionObjectID: queryProps.selectionObjectID,
								allUsers: selectedAll,
								...(queryProps.exclusionList?.length
									? { exclusionList: queryProps.exclusionList }
									: {}),
								...(queryProps.usersToExcludeSelectionId
									? { usersToExcludeSelectionId: queryProps.usersToExcludeSelectionId }
									: {}),
							});
							handleClose();
						}}
						size="large"
					>
						Select Group{selectedGroups.length > 1 ? "s" : ""}{" "}
						{selectedGroups.length > 0 && `(${selectedGroups.length})`}
					</ButtonLoading>
				</div>,
			]}
		>
			<div className="max-h-80 w-[400px] max-w-[400px] overflow-y-auto overflow-x-hidden">
				<div className="mb-4">
					<InputText
						value={searchTerm}
						onChange={(value) => setSearchTerm(value)}
						placeholder="Search groups..."
						leftIcon={faMagnifyingGlass}
						clearable={searchTerm.length > 0}
						aria-label="Search groups"
					/>
				</div>

				{/* Department rows — omit groups with no eligible users after exclusions */}
				{departments
					?.filter((department) => department.numUsers > 0)
					?.filter((department) => department.name.toLowerCase().includes(searchTerm.toLowerCase()))
					?.map((department) => (
						<div
							key={department._id}
							className="flex min-w-0 items-center gap-2 bg-neutral-0 px-1 py-2 [&:nth-child(even)]:bg-neutral-1"
						>
							<Checkbox
								id={department._id}
								checked={selectedGroups.includes(department._id)}
								onCheckedChange={(value) => {
									if (value) {
										setSelectedGroups([...selectedGroups, department._id]);
									} else {
										setSelectedGroups(selectedGroups.filter((group) => group !== department._id));
									}
								}}
							/>
							<div className="flex min-w-0 items-center gap-2">
								<div className="weight-regular flex min-w-0 items-center gap-2 text-paragraph-3">
									<label htmlFor={department._id} className="cursor-pointer truncate">
										{department.name}
									</label>
									<span className="shrink-0 text-paragraph-3 font-regular text-neutral-6">
										({department.numUsers} users)
									</span>
								</div>
							</div>
						</div>
					))}
			</div>
		</ContentModal>
	);
};

export default EmployeeSelectGroupsModal;
